// Storefront embed auto-mount orchestration.
//
// Split out of `index.ts` so the host-scanning logic is unit-testable
// without pulling in the heavy customizer chunk (maplibre et al.) and
// without a real browser DOM.  The DOM is reached through an injectable
// `MountDeps` (document / window / observer factory) — index.ts passes
// the real globals, tests pass plain fakes.
//
// Why this module exists at all: the previous `startAutoMount` only
// scanned on DOMContentLoaded + load.  Its own comment promised "a
// single MutationObserver pulse to catch hosts that appear later
// (lazy theme renderers)" — but no observer was ever wired up.  Themes
// that inject the product section after `load` (lazy sections, AJAX
// variant/section swaps) added the `.dfp-personalizer` host too late,
// so the trigger button never mounted ("directamente ni aparece").
// `startAutoMount` below adds that observer for real.

// Lo ÚNICO que este módulo importa: el sufijo de idioma para preguntar por
// el handle. Es el módulo LIGERO de locale (sin diccionarios), ya está en el
// grafo del entry y no hace nada al importarse, así que no mueve el
// presupuesto del bundle ni pide un DOM en los tests.
import { sufijoLocaleLigero } from './i18n-overrides.js';

const HOST_SELECTOR = '.dfp-personalizer, #dfp-personalizer';

/** Marks a host element as already processed so re-scans (the load
 *  event firing after DOMContentLoaded, or any observer pulse) never
 *  mount the same host twice.  Stored as `data-dfp-mounted` so it
 *  survives the `host.innerHTML = …` that the trigger renderer does. */
const MOUNTED_FLAG = 'dfpMounted';

/** A Shopify variant payload — emitted by the personalizer.liquid
 *  block via `data-variants` so the wizard can render its own format
 *  picker (price + availability + options) instead of falling back to
 *  the template's `productBases` list. */
export type ShopifyVariantPayload = {
  id: number;
  title: string;
  available: boolean;
  price: number;
  compare_at_price: number | null;
  sku: string | null;
  options: string[];
  featured_image: { src: string; alt: string } | null;
};

export type TriggerMount = {
  shop: string;
  handle: string | null;
  templateId: string | null;
  /** Pinned version id — set only by the admin's "Vista cliente" iframe so
   *  the embed loads the freshly-saved draft instead of the last published
   *  version. Real storefronts leave this null. */
  versionId: string | null;
  apiBase: string;
  variantId: string | null;
  /** All product variants (id + price + options + …) as serialised by
   *  the personalizer.liquid block.  Empty when running in standalone
   *  / admin preview mode — the wizard falls back to `doc.productBases`
   *  in that case. */
  variants: ShopifyVariantPayload[];
  /** Customer-facing product title (Shopify product.title). Used in
   *  the wizard header so the customer sees their product name. */
  productTitle: string | null;
  /** ISO 4217 currency code from the shop / cart context.  Used to
   *  format prices in the wizard. */
  currency: string | null;
  /** Existing Design.shareToken read from `?design=` on the URL.
   *  Drives resume + post-purchase-edit flow. */
  designToken: string | null;
  /** QUÉ estudio 3D abre el overlay cuando el motor es 3D: el de
   *  relieve (`mapa`, el de siempre), la figura estilo Funko (`figura`)
   *  o el llavero con nombre (`rotulo`). Lo anota
   *  `fetchTriggerContext` al resolver la plantilla, igual que anota
   *  `templateId`: la ficha real llega sin saberlo. Ausente = mapa. */
  studio3d?: 'mapa' | 'figura' | 'rotulo' | 'cabeza' | 'escultura';
  /** Y QUÉ PIEZA vende esta ficha cuando el estudio es el de mapas:
   *  la ciudad, el relieve o la carrera. Con ella puesta, el estudio no
   *  pinta la fila «Qué quieres» y arranca en lo que el producto vende.
   *  Lo anota `fetchTriggerContext`, como `studio3d`. Ausente = las
   *  tres fichas, que es lo que llevan las plantillas 3D de antes del
   *  28-sep-2026. */
  modo3d?: 'ciudad' | 'relieve' | 'carrera';
  /** La plantilla se abre en la pared de cuadros (`customerView.muro`) en
   *  vez del formulario. Lo anota `fetchTriggerContext`. */
  muro?: boolean;
  /** Token del enlace que EMITE EL COMERCIO desde un pedido, leído de
   *  `?dfpedit=`. Cuando está, manda sobre todo lo demás: dice qué
   *  plantilla, en qué versión y con qué tamaño abrir, y el guardado va
   *  a parar a una propuesta que el comercio aprueba. Ver
   *  `brandeate/resume-order-edit.ts`. */
  editToken: string | null;
};

/** The decision for a single host, computed purely from its dataset. */
export type MountPlan =
  | { kind: 'error'; message: string }
  | { kind: 'trigger'; mount: TriggerMount; hideDefaultCta: boolean };

/** Minimal host shape the resolver reads — a real HTMLElement satisfies
 *  it, and tests can pass `{ dataset: {…} }` without a DOM. */
type HostLike = { dataset: Record<string, string | undefined> };

/** Pure: turn a host's data-* attributes into a mount plan.  No DOM
 *  mutation, no globals — every branch is exercised in auto-mount.test.ts. */
export function resolveHostMount(
  host: HostLike,
  ctx: { designToken: string | null; editToken?: string | null },
): MountPlan {
  const shop = host.dataset.shop ?? '';   // empty = admin demo mode (no Shopify session)
  const handle = host.dataset.handle ?? null;
  const templateId = host.dataset.templateId ?? null;
  const versionId = host.dataset.versionId ?? null;
  const variantId = host.dataset.variant ?? null;
  // 2026-05: default switched from 'inline' to 'trigger'.
  // sep-2026: el modo `inline` LEGADO ya no existe. Un host que todavía
  // lo pida cae en el camino normal —el overlay— en vez de romperse: es
  // lo único que puede hacer un tema viejo que no vamos a poder editar.
  const modeRaw = (host.dataset.mode ?? 'trigger').toLowerCase();
  const mode = modeRaw === 'inline' ? 'trigger' : modeRaw;

  // An empty `data-api` (the merchant left api_url blank in the theme
  // block) is just as unusable as a missing one — treat both as the
  // error case rather than mounting a CTA whose every fetch 404s.
  const apiRaw = host.dataset.api;
  const apiBase = apiRaw && apiRaw.trim() ? apiRaw : undefined;
  if (apiBase === undefined || (!handle && !templateId)) {
    return {
      kind: 'error',
      message: 'Falta data-api y (data-handle o data-template-id) en el host del personalizer.',
    };
  }

  const hideDefaultCta = host.dataset.hideDefaultCta === '1';

  // Shopify variants — defensive parse: malformed JSON shouldn't break
  // the mount, the wizard gracefully falls back to productBases when
  // this is empty.
  let variants: ShopifyVariantPayload[] = [];
  const variantsRaw = host.dataset.variants;
  if (variantsRaw) {
    try { variants = JSON.parse(variantsRaw) as ShopifyVariantPayload[]; }
    catch (err) { console.warn('[personalizer-embed] data-variants parse failed', err); }
  }

  const mount: TriggerMount = {
    shop, handle, templateId, versionId, apiBase, variantId, variants,
    productTitle: host.dataset.productTitle ?? null,
    currency: host.dataset.currency ?? null,
    designToken: ctx.designToken,
    editToken: ctx.editToken ?? null,
  };

  // sep-2026: `inline-v2`, `inline-flat` y `auto` se retiraron con el
  // modo en línea. Cualquiera de los tres —y cualquier valor -que un
  // tema viejo traiga fijado— monta el botón: es la única presentación
  // que queda, y devolver un error por un ajuste guardado dejaría la
  // ficha sin personalizador.
  //
  // `auto` además costaba una PETICIÓN por ficha: preguntaba al API la
  // presentación del diseño antes de montar nada. Como la respuesta era
  // siempre la misma, el botón aparece ahora sin esperar a la red.
  return { kind: 'trigger', hideDefaultCta, mount };
}

/** Lo que el CTA necesita saber ANTES de que el cliente pulse. */
export type ContextoDelBoton = {
  /** Qué personalizador abrir. */
  kind: '2d' | '3d';
  /** Y, en 3D, qué estudio (ver `TriggerMount.studio3d`). Ausente =
   *  mapa, que es lo que llevan todas las plantillas 3D de antes. */
  studio?: 'mapa' | 'figura' | 'rotulo' | 'cabeza' | 'escultura';
  /** Y qué pieza, en el estudio de mapas (ver `TriggerMount.modo3d`). */
  modo3d?: 'ciudad' | 'relieve' | 'carrera';
  /** A qué plantilla atribuir la analítica. */
  templateId: string | null;
  /** Si el motor se SUPO, o es el 2D de por defecto.
   *
   *  Un fallo de red NO es una respuesta, y ésta es la diferencia entre
   *  las dos cosas. Sin este campo, una petición caída decidía el motor
   *  de la visita entera: medido en la ficha real del mapa 3D con la
   *  consulta abortada, pulsar «Personalizar» abría el personalizador
   *  2D —en un producto que no tiene documento 2D que pintar— sin un
   *  solo error en consola. Quien pulsa vuelve a preguntar. */
  seguro: boolean;
};

/** Sondeo corto. Un `fetch` sin reloj no lo pone el navegador (ver
 *  `fetch-timeout.ts`), y aquí el que espera es el CLIENTE con el dedo
 *  en el botón. Con AbortController a mano y no importando
 *  `fetch-timeout.ts` por lo mismo que `stale-bundle.ts`: esto vive en
 *  el entry, que se descarga en cada ficha y tiene presupuesto de
 *  bytes (bundle-budget.test.ts). */
const MS_MOTOR_AL_CARGAR = 8_000;
const MS_MOTOR_AL_PULSAR = 4_000;

/** Lo que este módulo lee del `customerView` de un documento. */
type CustomerView3D = { kind?: string; studio?: string; modo3d?: string };

/** Las tres piezas del estudio de mapas, tal y como las nombra el
 *  esquema. Cualquier otra cosa —una errata, un documento de antes— no
 *  es un modo: es «pinta las tres fichas». */
function modo3dLeido(v: unknown): 'ciudad' | 'relieve' | 'carrera' | undefined {
  return v === 'ciudad' || v === 'relieve' || v === 'carrera' ? v : undefined;
}

/** Resuelve de una sola petición el motor y la plantilla.
 *
 *  Van juntas a propósito. El id de plantilla hace falta para emitir
 *  `trigger_shown` (la impresión del botón, el denominador de todo el
 *  embudo) y sale del mismo JSON que ya se pedía para el motor; pedirlo
 *  aparte habría metido un segundo fetch en CADA ficha de producto de
 *  la tienda solo para contar.
 *
 *  Ante cualquier fallo devuelve el 2D —que es el motor que sabe abrir
 *  cualquier documento— pero marcado con `seguro: false`, para que
 *  quien decida algo importante con esto pueda volver a preguntar. */
export async function fetchTriggerContext(
  mount: TriggerMount,
  timeoutMs: number = MS_MOTOR_AL_CARGAR,
): Promise<ContextoDelBoton> {
  const base = mount.apiBase.replace(/\/+$/, '');
  const url = mount.templateId
    ? `${base}/storefront/templates/by-id/${encodeURIComponent(mount.templateId)}`
      + (mount.shop ? `?shop=${encodeURIComponent(mount.shop)}` : '')
    : mount.handle
      ? `${base}/storefront/templates/by-handle/${encodeURIComponent(mount.handle)}`
        // CON idioma: sin él, `elegirPorLocale` cae al grupo genérico y la
        // sonda resuelve la plantilla CASTELLANA gemela —el motor sale bien
        // porque la pt es un clon, pero `templateId` se fija con el id ES y
        // un producto vinculado SÓLO en pt daría 404 y `seguro: false`.
        // El idioma ya está resuelto aquí: `detectarYFijarLocale(host)` corre
        // en el entry antes de esta sonda. Memoria [complementos-404-en-portugues].
        + `?shop=${encodeURIComponent(mount.shop)}${sufijoLocaleLigero()}`
      : null;
  // Sin URL no hay nada que preguntar, y eso NO es un fallo de red: es
  // un host sin handle ni plantilla, que el 2D resuelve por su cuenta.
  if (!url) return { kind: '2d', studio: 'mapa', templateId: mount.templateId, seguro: true };
  const ctrl = new AbortController();
  const timer = setTimeout(() => ctrl.abort(), timeoutMs);
  try {
    const res = await fetch(url, { credentials: 'omit', signal: ctrl.signal });
    if (!res.ok) return { kind: '2d', studio: 'mapa', templateId: mount.templateId, seguro: false };
    const json = await res.json() as {
      template?: { id?: string; document?: { customerView?: CustomerView3D & { muro?: unknown } } };
      designs?: Array<{ templateId?: string; document?: { customerView?: CustomerView3D & { muro?: unknown } } }>;
    };
    // by-handle devuelve TODOS los diseños vinculados ordenados por
    // `position` y gana el primero; by-id devuelve uno bajo `template`.
    const first = json.designs?.[0];
    const kind = first?.document?.customerView?.kind
      ?? json.template?.document?.customerView?.kind;
    const studioLeido = first?.document?.customerView?.studio
      ?? json.template?.document?.customerView?.studio;
    const studio = studioLeido === 'figura' ? 'figura'
      : studioLeido === 'rotulo' ? 'rotulo'
      : studioLeido === 'cabeza' ? 'cabeza'
      : studioLeido === 'escultura' ? 'escultura'
      : 'mapa';
    // LA PIEZA, y con la misma precedencia que el estudio. Lo que no
    // sea uno de los tres literales se deja sin poner: el estudio
    // entonces pinta sus tres fichas, que es lo que hacían todas las
    // plantillas 3D hasta el 28-sep-2026.
    const modo3d = modo3dLeido(
      first?.document?.customerView?.modo3d ?? json.template?.document?.customerView?.modo3d,
    );
    mount.modo3d = modo3d;
    mount.muro = Boolean(first?.document?.customerView?.muro ?? json.template?.document?.customerView?.muro);
    // Se anota en el mount aquí mismo, por los DOS caminos que
    // preguntan (el sondeo al cargar y el reintento al pulsar): el
    // overlay lo lee del mount para saber qué página cargar.
    mount.studio3d = studio;
    const templateId = first?.templateId
      ?? json.template?.id
      ?? mount.templateId;
    return {
      kind: kind === '3d' ? '3d' : '2d',
      studio,
      modo3d,
      templateId: templateId ?? null,
      // Una respuesta sin `customerView.kind` es de una plantilla 2D de
      // las de siempre: eso SÍ se sabe.
      seguro: true,
    };
  } catch {
    return { kind: '2d', studio: 'mapa', templateId: mount.templateId, seguro: false };
  } finally {
    clearTimeout(timer);
  }
}

/** El motor, preguntando OTRA VEZ si la primera no lo supo.
 *
 *  Lo usan los dos sitios que abren el personalizador de verdad —el
 *  clic en el botón y la apertura automática de los enlaces de correo—,
 *  y por eso el reintento vive aquí y no en cada uno. La segunda
 *  pregunta va con reloj más corto: al otro lado hay alguien esperando
 *  a que se abra algo. */
export async function fetchEngineKind(
  mount: TriggerMount,
  yaSabido?: ContextoDelBoton,
): Promise<'2d' | '3d'> {
  if (yaSabido?.seguro) return yaSabido.kind;
  const otra = await fetchTriggerContext(mount, MS_MOTOR_AL_PULSAR);
  if (!otra.seguro) {
    // Se abre el 2D igual —es el único que puede abrir algo— pero se
    // DICE. Un producto 3D abriéndose en el editor de carteles es un
    // fallo mudo, y mudo es como estuvo.
    console.warn('[personalizer-embed] no se pudo saber el motor; abriendo el 2D');
  }
  if (otra.templateId && !mount.templateId) mount.templateId = otra.templateId;
  return otra.kind;
}

/** The side-effecting collaborators, injected so tests stay DOM-free. */
export type MountDeps = {
  doc: Document;
  win: Window & typeof globalThis;
  /** La hoja de PAGE-LOAD, no la del formulario. Se llama en cada
   *  scan; si alguien la reapunta a `injectStyles()` (styles.ts) el
   *  entry vuelve a engordar 165 KB. Ver styles/trigger.ts. */
  injectTriggerStyles: () => void;
  mountTrigger: (host: HTMLElement, mount: TriggerMount) => void;
  /** Abre el overlay sin pasar por el botón: enlaces «Editar» del correo
   *  y `?open=personalizer`. */
  openOverlay: (mount: TriggerMount) => void;
  /** Called once per host the moment it resolves to a real mount (any
   *  mode), carrying that host's resolved api/shop.  index.ts uses it to
   *  start the settings bridge: the loader liquid injects the host LATE
   *  (after an async product→template check), so a one-shot synchronous
   *  querySelector at module-eval time misses it and the merchant's saved
   *  logo/name/colours are never fetched on the live storefront.  Routing
   *  the bridge boot through the same observer-backed scan that mounts the
   *  CTA makes it resilient to the late host.  The bridge dedups its own
   *  start, so firing this on every host mount is safe. */
  onHostMount?: (info: { apiBase: string; shop: string }) => void;
  /** Override the observer constructor in tests. Defaults to the real one. */
  createObserver?: (cb: MutationCallback) => MutationObserver;
  /** ¿El `?design=` de la barra lo creó ESTE navegador? Decide si un
   *  token abre el personalizador solo al cargar (ver abajo). Inyectada
   *  —como el resto del DOM de este módulo— para que siga siendo
   *  probable sin navegador; sin ella, un token abre siempre, que es el
   *  comportamiento anterior a ago-2026. */
  isOwnedDesign?: (token: string) => boolean;
};

/** Sella el CTA nativo del tema como definitivamente oculto.
 *
 *  Se llama DESPUÉS de montar, no antes, y ese orden es el arreglo
 *  (sep-2026): esta marca colapsa el botón del comercio, así que
 *  ponerla antes de que exista el nuestro deja un HUECO donde estaba
 *  —medio segundo en el camino `auto`, que se para a preguntar la
 *  presentación—. Lo que se ve durante ese rato lo pinta la fase de
 *  espera del cargador (`html[data-dfp-cta="pending"]`, en
 *  personalizer-loader.liquid), que tapa el botón conservando su caja.
 *
 *  Y por eso se retira aquí esa fase: a partir de este punto la manda
 *  esta marca. Si el cargador es de una versión anterior no habrá nada
 *  que quitar, y si el embed lo es, el `display:none` de «activo» gana
 *  igualmente sobre el `visibility:hidden` de la espera — las dos
 *  mitades se pueden desplegar en cualquier orden. */
function markCtaHidden(doc: Document): void {
  doc.body.setAttribute('data-personalizer-active', '1');
  endCtaWait(doc);
}

/** Cierra la fase de espera del cargador: la marca del <html> y la
 *  pastilla que pintaba mientras tanto.
 *
 *  La pastilla vive DENTRO del host, así que `mountTriggerButton` la
 *  borra sola al escribir su `innerHTML` — pero no todos los caminos
 *  escriben el host (la presentación que no se resuelve, por ejemplo),
 *  y una pastilla huérfana encima del botón del comercio recién
 *  devuelto es peor que no haberla puesto. Se limpia aquí, una vez,
 *  para todos. */
/** ¿Teeinblue tiene PUBLICADO este producto?
 *
 *  Su SDK se carga en toda la tienda, así que «está Teeinblue» no dice
 *  nada: lo que decide es su bandera POR PRODUCTO. Medido el 6-sep-2026
 *  en dondefue.com — «Cuadro Fase Lunar» y «Donde nos Conocimos» la
 *  traen en `true`, y «Cuadro Mapa Estelar», que es nuestro, en `false`.
 *
 *  Ojo con la tentación de mirar el metafield `teeinblue.platform_product`
 *  a secas: está en TODOS los productos que alguna vez sincronizaron con
 *  ellos, incluidos los nuestros (con `published_at: null`). Lo que
 *  separa unos de otros es esa fecha, y es justo lo que su bandera ya
 *  resuelve. */
function teeinblueManda(win: MountDeps['win']): boolean {
  try {
    const w = win as unknown as { teeinblueCampaign?: { isTeeInBlueProduct?: boolean } };
    return w.teeinblueCampaign?.isTeeInBlueProduct === true;
  } catch {
    return false;
  }
}

function endCtaWait(doc: Document): void {
  doc.documentElement.removeAttribute('data-dfp-cta');
  for (const p of doc.querySelectorAll('.dfp-cta-espera')) p.remove();
}

/** Quita el velo que el cargador pone sobre la ficha cuando la URL pide
 *  abrir el personalizador (`html[data-dfp-abriendo]`, en
 *  personalizer-loader.liquid).
 *
 *  Cuando el overlay SÍ se abre lo quita el propio cargador, que lo ve
 *  aparecer. Esto es para cuando se sabe que NO se va a abrir: sin ello
 *  el cliente miraría una pantalla vacía hasta el seguro de diez
 *  segundos. */
export function soltarVeloDeApertura(doc: Document): void {
  doc.documentElement.removeAttribute('data-dfp-abriendo');
}

/** ¿Esta vista cayó en la rama B del A/B «editor directo»?
 *
 *  Lo decide el cargador del tema (personalizer-loader.liquid, entre las
 *  marcas `ab-editor:inicio` y `ab-editor:fin`), en el <head> y antes del
 *  primer pintado, porque tiene que poner el velo antes de que se vea la
 *  ficha. Aquí sólo se lee: la B abre el personalizador igual que
 *  `?open=personalizer`, y la A —y cualquier ficha sin test— no deja
 *  nada en `window`. Sólo vale el `true` literal: lo que no venga del
 *  cargador no abre nada. */
export function abreAlLlegar(win: Window): boolean {
  try {
    return (win as unknown as { __dfpAbrirAlLlegar?: unknown }).__dfpAbrirAlLlegar === true;
  } catch {
    return false;
  }
}

/** Scan the document for personalizer hosts and mount each one exactly
 *  once.  Idempotent: safe to call repeatedly (load event, observer
 *  pulses) — already-mounted hosts are skipped via the data flag. */
export function scanAndMount(deps: MountDeps): void {
  deps.injectTriggerStyles();

  // `?design=<token>` lets the customer reopen a saved/purchased design.
  // `?open=personalizer` opens the overlay straight away — lo llevan el
  // enlace de "Compartir" y el "Editar mi diseño" del email.
  //
  // Un token por sí solo abre el personalizador SOLO si no lo creó este
  // navegador. Desde ago-2026 el token vive en la barra de cualquiera
  // que edite (brandeate/design-session.ts), así que «hay token» dejó de
  // significar «vengo a ver este diseño»: puede ser sencillamente que el
  // cliente haya recargado su propia ficha de producto, y a quien vuelve
  // a mirar el precio no se le abre el editor en la cara. El enlace de
  // otra persona sí abre —es lo que esa persona quería enseñar— y los
  // enlaces antiguos, que solo llevan `?design=`, siguen abriéndose en
  // cualquier dispositivo que no los creara.
  const params = new URLSearchParams(deps.win.location.search);
  const urlDesignToken = params.get('design');
  // `?dfpedit=` — el enlace que el comercio manda desde un pedido. Abre
  // SIEMPRE, y no necesita `open=personalizer`: ese parámetro existe
  // para desempatar un `?design=` que puede ser del propio navegador del
  // cliente, y este token sólo lo pone el comercio. Ver
  // apps/api/src/order-edit-link.ts.
  const urlEditToken = params.get('dfpedit');
  const isOwn = deps.isOwnedDesign ?? ((): boolean => false);
  const wantsAutoOpen = urlEditToken != null
    || params.get('open') === 'personalizer'
    || (urlDesignToken != null && !isOwn(urlDesignToken))
    || abreAlLlegar(deps.win);

  // ── Teeinblue manda ───────────────────────────────────────────────
  // Si el producto está PUBLICADO en Teeinblue, aquí no se monta nada.
  // Dos personalizadores en una ficha son dos botones que dicen lo mismo
  // y llevan a sitios distintos, y el cliente elige mal la mitad de las
  // veces.
  //
  // La señal es `isTeeInBlueProduct`, que su propio script calcula por
  // producto —NO que su SDK esté cargado, que lo está en toda la tienda—.
  // El cargador del tema ya decide esto en el servidor con el metafield
  // (sin parpadeo); esto es la red por si el comercio coloca el bloque a
  // mano en el editor de temas, que no pasa por ahí.
  //
  // Y se devuelve el botón del tema: si nos plantamos SIN quitar la fase
  // de espera, la ficha se queda con el «Añadir al carrito» tapado y sin
  // nada que lo sustituya — peor que los dos botones.
  if (teeinblueManda(deps.win)) {
    // Y SE QUITA EL HOST, que no es limpieza: es lo único que devuelve
    // el botón del tema. El de Donde Fue lo colapsa con su propia regla
    // `:has(.dfp-personalizer)`, así que mientras el nodo exista su
    // «Añadir al carrito» no vuelve, por mucho que nosotros quitemos
    // nuestra marca. Y el nodo lo inyecta el cargador de la extensión de
    // tema, que se despliega aparte y puede ir por detrás de esto.
    //
    // Pasó tal cual el 6-sep-2026: con la marca quitada y el host
    // puesto, «Cuadro Fase Lunar» se quedó sin ninguna forma de comprar.
    for (const host of deps.doc.querySelectorAll<HTMLElement>(HOST_SELECTOR)) {
      host.remove();
    }
    endCtaWait(deps.doc);
    soltarVeloDeApertura(deps.doc);
    deps.doc.body?.removeAttribute('data-personalizer-active');
    console.info('[personalizer-embed] no monto: Teeinblue tiene publicado este producto.');
    return;
  }

  const hosts = deps.doc.querySelectorAll<HTMLElement>(HOST_SELECTOR);
  for (const host of hosts) {
    if (host.dataset[MOUNTED_FLAG] === '1') continue;

    const plan = resolveHostMount(host, { designToken: urlDesignToken, editToken: urlEditToken });

    if (plan.kind === 'error') {
      // El host está mal formado: no habrá botón nuestro, así que se
      // devuelve el del tema quitando la fase de espera del cargador.
      endCtaWait(deps.doc);
      soltarVeloDeApertura(deps.doc);
      host.innerHTML = `<pre class="dfp-err">${plan.message}</pre>`;
      // Flag it so the observer doesn't reprocess this exact (broken)
      // element on every mutation.  A theme that later re-renders the
      // block produces a NEW element without the flag, which we retry.
      host.dataset[MOUNTED_FLAG] = '1';
      continue;
    }

    // Mark mounted BEFORE running the (possibly re-entrant) mount so an
    // observer pulse fired mid-mount can't double-mount the same host.
    host.dataset[MOUNTED_FLAG] = '1';

    // Kick the settings bridge with THIS host's api/shop.  The host is
    // frequently injected late (loader liquid → async checkLinked), so the
    // synchronous bridge boot in index.ts runs before it exists; this
    // observer-backed path is the reliable trigger on real storefronts.
    deps.onHostMount?.({ apiBase: plan.mount.apiBase, shop: plan.mount.shop });

    // Wrap mount + openOverlay en try/catch para que un throw silencioso
    // (ej. style spec malformada, módulo lazy que falla al cargar) no
    // deje al cliente con el placeholder "Cargando personalizador..."
    // del preview.html sin ningún feedback de qué ha pasado.  El error
    // se vuelca al console + se renderiza un mensaje en el host para
    // que el merchant pueda diagnosticar sin DevTools.
    try {
      {
        deps.mountTrigger(host, plan.mount);
        if (wantsAutoOpen) deps.openOverlay(plan.mount);
        // Montado y sin abrir: si el cargador tapó la ficha creyendo que
        // se abriría, que no espere al seguro de diez segundos.
        else soltarVeloDeApertura(deps.doc);
      }
      // Sólo ahora, con nuestro botón ya en la página. Si el montaje
      // hubiera lanzado, el CTA del tema sigue visible — mejor que el
      // comercio vea su botón de comprar que una ficha sin ninguno.
      if (plan.hideDefaultCta) markCtaHidden(deps.doc);
      console.info('[personalizer-embed] mounted', { kind: plan.kind, host: host.id || host.className });
    } catch (err) {
      endCtaWait(deps.doc);
      soltarVeloDeApertura(deps.doc);
      const msg = err instanceof Error ? err.message : String(err);
      console.error('[personalizer-embed] mount failed', err);
      host.innerHTML = `<pre class="dfp-err" style="padding:16px;background:#fee;color:#900;border-radius:6px;margin:48px;white-space:pre-wrap;font-family:ui-monospace,monospace;font-size:12px;">Personalizer: error al montar.\n${msg}\n\nAbre DevTools (F12) → Consola para más detalles.</pre>`;
    }
  }
}

/** Wire the mount lifecycle: an initial scan at the right document
 *  readiness, plus a long-lived MutationObserver so hosts injected
 *  after load (lazy theme sections, AJAX section re-render on variant
 *  change) still get the button.  Returns a disposer (used by tests;
 *  the storefront never tears down). */
export function startAutoMount(deps: MountDeps): () => void {
  const run = (): void => scanAndMount(deps);

  // Defer scripts execute after HTML parsing but before DOMContentLoaded,
  // so readyState is "interactive" at this point, NOT "loading". Only run
  // synchronously when the document is fully "complete" (we missed
  // nothing); otherwise wait so we run AFTER the loader's own
  // DOMContentLoaded host-creation script.
  if (deps.doc.readyState === 'complete') {
    run();
  } else {
    deps.doc.addEventListener('DOMContentLoaded', run, { once: true });
    deps.win.addEventListener('load', run, { once: true });
  }

  const makeObserver = deps.createObserver
    ?? ((cb: MutationCallback) => new deps.win.MutationObserver(cb));

  let scheduled = false;
  const observer = makeObserver((records) => {
    if (scheduled) return;
    // Cheap pre-filter: only schedule a re-scan when an added node is (or
    // contains) a personalizer host, so unrelated DOM churn is a no-op.
    if (!recordsTouchHost(records)) return;
    scheduled = true;
    queueMicrotask(() => { scheduled = false; run(); });
  });
  observer.observe(deps.doc.documentElement, { childList: true, subtree: true });

  return () => observer.disconnect();
}

function recordsTouchHost(records: MutationRecord[]): boolean {
  for (const rec of records) {
    for (const node of rec.addedNodes) {
      if (node.nodeType !== 1) continue;
      const el = node as Element;
      if (el.matches?.(HOST_SELECTOR) || el.querySelector?.(HOST_SELECTOR)) return true;
    }
  }
  return false;
}
